import {
  HHAlert,
  HHButton,
  HHStack,
} from '@hinge-health/react-component-library';
import { FormEvent, useState } from 'react';
import { CreateConfigurationInput } from '../domain/types';
import { parseConfigurationPaste } from '../utils/configuration-paste.utils';
import { JsonEditor } from './json-editor';

export const CONFIGURATION_PASTE_FORM_TEST_ID = 'pdg-configuration-paste-form';
export const CONFIGURATION_PASTE_EDITOR_TEST_ID =
  'pdg-configuration-paste-editor';
export const CONFIGURATION_PASTE_ERRORS_TEST_ID =
  'pdg-configuration-paste-errors';
export const CONFIGURATION_PASTE_SUBMIT_TEST_ID =
  'pdg-configuration-paste-submit-button';
export const CONFIGURATION_PASTE_CANCEL_TEST_ID =
  'pdg-configuration-paste-cancel-button';
export const CONFIGURATION_PASTE_SERVER_ERROR_TEST_ID =
  'pdg-configuration-paste-server-error-alert';

export const CONFIGURATION_PASTE_LABEL = 'Configuration (JSON)';
export const CONFIGURATION_PASTE_HELPER_TEXT =
  'Paste a whole configuration file: { name, dataProvider, definition, actions }, e.g. a reviewed file from partner-data-gateway-service/configurations/.';
export const CONFIGURATION_PASTE_ERRORS_HEADING =
  'Fix these before creating the configuration:';

export interface ConfigurationJsonPasteFormProps {
  onSubmit: (values: CreateConfigurationInput) => void;
  onCancel: () => void;
  isSaving?: boolean;
  /** Mutation failure, rendered above the buttons. */
  error?: string | null;
  submitLabel?: string;
}

/**
 * Create a configuration by pasting its JSON.
 *
 * The parsed payload is submitted verbatim: it does not pass through the
 * Action Builder, which rebuilds each action from the catalog and would
 * normalise (or drop) fields a reviewed file carries on purpose. Checks run on
 * submit, like the form, so the operator is never interrupted while pasting.
 */
export const ConfigurationJsonPasteForm = ({
  onSubmit,
  onCancel,
  isSaving = false,
  error = null,
  submitLabel = 'Create configuration',
}: ConfigurationJsonPasteFormProps): JSX.Element => {
  const [text, setText] = useState('');
  const [errors, setErrors] = useState<string[]>([]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();

    const result = parseConfigurationPaste(text);

    setErrors(result.errors);

    if (result.value) {
      onSubmit(result.value);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      noValidate
      data-testid={CONFIGURATION_PASTE_FORM_TEST_ID}
    >
      <HHStack gap={3}>
        {/*
          The editor's own blur check covers "is this a JSON object"; the
          contract errors are listed below instead of in its helper text so
          several can be shown at once.
        */}
        <JsonEditor
          label={CONFIGURATION_PASTE_LABEL}
          helperText={CONFIGURATION_PASTE_HELPER_TEXT}
          name="configuration"
          testId={CONFIGURATION_PASTE_EDITOR_TEST_ID}
          value={text}
          disabled={isSaving}
          onChange={(value): void => {
            setText(value);
            setErrors([]);
          }}
        />

        {errors.length > 0 && (
          <HHAlert
            hhVariant="variant-bypass"
            variant="outlined"
            severity="error"
            data-testid={CONFIGURATION_PASTE_ERRORS_TEST_ID}
          >
            {CONFIGURATION_PASTE_ERRORS_HEADING}
            <ul>
              {errors.map(message => (
                <li key={message}>{message}</li>
              ))}
            </ul>
          </HHAlert>
        )}

        {error && (
          <HHAlert
            hhVariant="variant-bypass"
            variant="outlined"
            severity="error"
            data-testid={CONFIGURATION_PASTE_SERVER_ERROR_TEST_ID}
          >
            {error}
          </HHAlert>
        )}

        <HHStack direction="row" gap={2} justifyContent="flex-end">
          <HHButton
            hhVariant="variant-bypass"
            variant="outlined"
            type="button"
            disabled={isSaving}
            onClick={onCancel}
            data-testid={CONFIGURATION_PASTE_CANCEL_TEST_ID}
          >
            Cancel
          </HHButton>
          <HHButton
            hhVariant="variant-bypass"
            variant="contained"
            type="submit"
            disabled={isSaving}
            data-testid={CONFIGURATION_PASTE_SUBMIT_TEST_ID}
          >
            {isSaving ? 'Saving…' : submitLabel}
          </HHButton>
        </HHStack>
      </HHStack>
    </form>
  );
};
